/**
 * Pack illustration path data.
 *
 * Split out from PackArt.tsx so that file exports a component and nothing else,
 * which is what React Fast Refresh needs to hot-swap it.
 *
 * Ported verbatim from the `<symbol id="pk-*">` set in ChiCare-Mockups/.
 */

import type { PackArtKey } from '@chicare/shared';

export const packArtPaths: Record<PackArtKey, React.ReactNode> = {
  para: (
    <g>
      <path d="M22 30 L50 20 L78 30 L78 82 L50 92 L22 82 Z" fill="#F2F6FC" />
      <path d="M22 30 L50 20 L78 30 L50 40 Z" fill="#FDFEFF" />
      <path d="M50 40 L78 30 L78 82 L50 92 Z" fill="#DCE6F5" />
      <path d="M22 30 L50 40 L50 92 L22 82 Z" fill="#FAFCFE" />
      <path d="M22 46 L50 56 L50 65 L22 55 Z" fill="#0F52D9" />
      <path d="M50 56 L78 46 L78 55 L50 65 Z" fill="#0A2E80" />
      <rect x="27" y="60" width="18" height="2.6" rx="1.3" fill="#0A2E80" opacity=".55" transform="rotate(19.5 27 60)" />
      <rect x="27" y="67" width="12" height="2.2" rx="1.1" fill="#8B9AB2" opacity=".7" transform="rotate(19.5 27 67)" />
      <rect x="27" y="73" width="15" height="2.2" rx="1.1" fill="#8B9AB2" opacity=".5" transform="rotate(19.5 27 73)" />
      <g transform="translate(58 63) rotate(-20)">
        <rect x="0" y="0" width="15" height="7" rx="3.5" fill="#fff" />
        <path d="M7.5 0 h4 a3.5 3.5 0 0 1 0 7 h-4 z" fill="#B9CBEC" />
      </g>
      <path d="M22 30 L50 20 L78 30 L78 82 L50 92 L22 82 Z" fill="none" stroke="#C3D0E3" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M50 40 L50 92 M22 30 L50 40 L78 30" fill="none" stroke="#C3D0E3" strokeWidth="1" />
    </g>
  ),
  amox: (
    <g>
      <path d="M24 28 L50 18 L76 28 L76 84 L50 94 L24 84 Z" fill="#F4F7FB" />
      <path d="M24 28 L50 18 L76 28 L50 38 Z" fill="#FDFEFF" />
      <path d="M50 38 L76 28 L76 84 L50 94 Z" fill="#D9E2EE" />
      <path d="M24 44 L50 54 L50 63 L24 53 Z" fill="#2A6E63" />
      <path d="M50 54 L76 44 L76 53 L50 63 Z" fill="#1C4F48" />
      <rect x="29" y="58" width="17" height="2.6" rx="1.3" fill="#2A6E63" opacity=".6" transform="rotate(19.5 29 58)" />
      <rect x="29" y="65" width="11" height="2.2" rx="1.1" fill="#8B9AB2" opacity=".65" transform="rotate(19.5 29 65)" />
      <rect x="29" y="71" width="14" height="2.2" rx="1.1" fill="#8B9AB2" opacity=".45" transform="rotate(19.5 29 71)" />
      <g transform="translate(57 62) rotate(-20)">
        <rect x="0" y="0" width="16" height="7" rx="3.5" fill="#EDF3F2" />
        <path d="M8 0 h4.5 a3.5 3.5 0 0 1 0 7 H8 z" fill="#2A6E63" />
      </g>
      <path d="M24 28 L50 18 L76 28 L76 84 L50 94 L24 84 Z" fill="none" stroke="#C3D0E3" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M50 38 L50 94 M24 28 L50 38 L76 28" fill="none" stroke="#C3D0E3" strokeWidth="1" />
    </g>
  ),
  blister: (
    <g>
      <rect x="18" y="24" width="64" height="52" rx="6" fill="#EEF2F8" stroke="#C3D0E3" strokeWidth="1.4" />
      <rect x="18" y="24" width="64" height="10" rx="6" fill="#DCE6F5" />
      <g fill="#fff" stroke="#C3D0E3" strokeWidth="1">
        <circle cx="31" cy="46" r="7" />
        <circle cx="50" cy="46" r="7" />
        <circle cx="69" cy="46" r="7" />
        <circle cx="31" cy="63" r="7" />
        <circle cx="50" cy="63" r="7" />
        <circle cx="69" cy="63" r="7" />
      </g>
      <g fill="#B9CBEC">
        <circle cx="31" cy="46" r="4" />
        <circle cx="50" cy="46" r="4" />
        <circle cx="69" cy="46" r="4" />
        <circle cx="31" cy="63" r="4" />
        <circle cx="50" cy="63" r="4" />
      </g>
      <circle cx="69" cy="63" r="4" fill="#0F52D9" opacity=".35" />
      <rect x="24" y="27" width="22" height="3" rx="1.5" fill="#0F52D9" opacity=".5" />
    </g>
  ),
  syrup: (
    <g>
      <rect x="42" y="14" width="16" height="10" rx="2" fill="#0A2E80" />
      <rect x="44.5" y="22" width="11" height="7" fill="#C3D0E3" />
      <path d="M34 30 h32 a6 6 0 0 1 6 6 v46 a6 6 0 0 1 -6 6 h-32 a6 6 0 0 1 -6 -6 v-46 a6 6 0 0 1 6 -6 z" fill="#EDF2F9" stroke="#C3D0E3" strokeWidth="1.3" />
      <path d="M31 58 h38 v22 a6 6 0 0 1 -6 6 h-26 a6 6 0 0 1 -6 -6 z" fill="#C9A15E" opacity=".55" />
      <rect x="34" y="38" width="32" height="20" rx="3" fill="#fff" stroke="#DCE3EE" strokeWidth="1" />
      <rect x="38" y="42" width="20" height="3" rx="1.5" fill="#0F52D9" opacity=".65" />
      <rect x="38" y="48" width="14" height="2.4" rx="1.2" fill="#8B9AB2" opacity=".7" />
      <rect x="38" y="53" width="17" height="2.4" rx="1.2" fill="#8B9AB2" opacity=".45" />
    </g>
  ),
  vit: (
    <g>
      <rect x="40" y="12" width="20" height="9" rx="2.5" fill="#B5761F" />
      <path d="M32 21 h36 a5 5 0 0 1 5 5 v56 a6 6 0 0 1 -6 6 h-34 a6 6 0 0 1 -6 -6 v-56 a5 5 0 0 1 5 -5 z" fill="#F2F6FC" stroke="#C3D0E3" strokeWidth="1.3" />
      <rect x="31" y="36" width="38" height="34" rx="3" fill="#fff" stroke="#DCE3EE" strokeWidth="1" />
      <circle cx="43" cy="48" r="7" fill="#E8A33D" opacity=".85" />
      <path d="M43 43.5 v9 M38.5 48 h9" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      <rect x="53" y="44" width="13" height="3" rx="1.5" fill="#0A2E80" opacity=".7" />
      <rect x="53" y="50" width="9" height="2.4" rx="1.2" fill="#8B9AB2" />
      <rect x="36" y="60" width="28" height="2.4" rx="1.2" fill="#8B9AB2" opacity=".55" />
      <rect x="36" y="65" width="20" height="2.4" rx="1.2" fill="#8B9AB2" opacity=".35" />
    </g>
  ),
  caps: (
    <g>
      <path d="M30 22 h40 v62 a6 6 0 0 1 -6 6 h-28 a6 6 0 0 1 -6 -6 z" fill="#EEF3FA" stroke="#C3D0E3" strokeWidth="1.3" />
      <rect x="36" y="12" width="28" height="11" rx="3" fill="#0F52D9" />
      <rect x="33" y="34" width="34" height="30" rx="3" fill="#fff" stroke="#DCE3EE" />
      <rect x="37" y="39" width="20" height="3.4" rx="1.7" fill="#0A2E80" opacity=".75" />
      <rect x="37" y="46" width="14" height="2.6" rx="1.3" fill="#8B9AB2" opacity=".7" />
      <g transform="translate(37 53) rotate(-8)">
        <rect width="15" height="6.5" rx="3.25" fill="#EDF2F9" />
        <path d="M7.5 0 h4.25 a3.25 3.25 0 0 1 0 6.5 H7.5 z" fill="#0F52D9" opacity=".7" />
      </g>
      <rect x="36" y="70" width="28" height="2.4" rx="1.2" fill="#8B9AB2" opacity=".45" />
      <rect x="36" y="75" width="18" height="2.4" rx="1.2" fill="#8B9AB2" opacity=".3" />
    </g>
  ),
  bp: (
    <g>
      <rect x="18" y="30" width="52" height="42" rx="7" fill="#F1F5FA" stroke="#C3D0E3" strokeWidth="1.3" />
      <rect x="25" y="37" width="38" height="22" rx="3" fill="#0A2E80" />
      <text x="30" y="52" fontFamily="monospace" fontSize="12" fill="#7FE8C0" fontWeight="bold">120</text>
      <text x="52" y="52" fontFamily="monospace" fontSize="8" fill="#8FB3F0">/80</text>
      <circle cx="30" cy="65" r="3.2" fill="#0F52D9" />
      <circle cx="40" cy="65" r="3.2" fill="#C3D0E3" />
      <circle cx="50" cy="65" r="3.2" fill="#C3D0E3" />
      <path d="M70 44 q16 2 14 14 q-2 12 -16 14 q-8 1 -12 -4" fill="none" stroke="#B9CBEC" strokeWidth="4" strokeLinecap="round" />
      <path d="M56 70 h20 a6 6 0 0 1 6 6 v6 a6 6 0 0 1 -6 6 h-20 a6 6 0 0 1 -6 -6 v-6 a6 6 0 0 1 6 -6 z" fill="#DCE6F5" stroke="#C3D0E3" strokeWidth="1.2" />
    </g>
  ),
  thermo: (
    <g>
      <rect x="20" y="42" width="60" height="16" rx="8" fill="#F4F7FB" stroke="#C3D0E3" strokeWidth="1.3" />
      <rect x="24" y="46" width="26" height="8" rx="4" fill="#0A2E80" />
      <text x="27" y="53" fontFamily="monospace" fontSize="7" fill="#7FE8C0" fontWeight="bold">36.6</text>
      <circle cx="70" cy="50" r="5" fill="#0F52D9" opacity=".28" />
      <path d="M56 50 h8" stroke="#C3D0E3" strokeWidth="2" strokeLinecap="round" />
      <rect x="34" y="62" width="32" height="3" rx="1.5" fill="#C3D0E3" opacity=".6" />
    </g>
  ),
  firstaid: (
    <g>
      <path d="M20 34 h60 a7 7 0 0 1 7 7 v34 a7 7 0 0 1 -7 7 h-60 a7 7 0 0 1 -7 -7 v-34 a7 7 0 0 1 7 -7 z" fill="#F2F6FC" stroke="#C3D0E3" strokeWidth="1.3" />
      <path d="M38 34 v-6 a4 4 0 0 1 4 -4 h16 a4 4 0 0 1 4 4 v6" fill="none" stroke="#C3D0E3" strokeWidth="3" />
      <rect x="13" y="52" width="74" height="5" fill="#DCE6F5" />
      <path d="M45 44 h10 v8 h8 v10 h-8 v8 h-10 v-8 h-8 v-10 h8 z" fill="#0F52D9" />
    </g>
  ),
  inhaler: (
    <g>
      <path d="M40 18 h20 a5 5 0 0 1 5 5 v30 h-30 v-30 a5 5 0 0 1 5 -5 z" fill="#EDF2F9" stroke="#C3D0E3" strokeWidth="1.3" />
      <path d="M35 53 h30 v14 a6 6 0 0 1 -6 6 h-6 v12 a5 5 0 0 1 -5 5 h-2 a5 5 0 0 1 -5 -5 v-12 h-6 z" fill="#0F52D9" opacity=".85" />
      <rect x="42" y="26" width="16" height="3" rx="1.5" fill="#0A2E80" opacity=".55" />
      <rect x="42" y="33" width="11" height="2.4" rx="1.2" fill="#8B9AB2" />
    </g>
  ),
  test: (
    <g>
      <rect x="22" y="30" width="56" height="40" rx="5" fill="#F4F7FB" stroke="#C3D0E3" strokeWidth="1.3" />
      <rect x="22" y="30" width="56" height="11" rx="5" fill="#7A5AA8" opacity=".8" />
      <rect x="29" y="47" width="26" height="3.4" rx="1.7" fill="#0A2E80" opacity=".7" />
      <rect x="29" y="54" width="17" height="2.6" rx="1.3" fill="#8B9AB2" opacity=".7" />
      <rect x="29" y="60" width="21" height="2.6" rx="1.3" fill="#8B9AB2" opacity=".45" />
      <g transform="translate(58 46)">
        <rect width="14" height="20" rx="3" fill="#fff" stroke="#C3D0E3" />
        <rect x="4" y="4" width="6" height="2" fill="#0F52D9" />
        <rect x="4" y="8" width="6" height="2" fill="#0F52D9" opacity=".4" />
      </g>
    </g>
  ),
};

export const packArtKeys = Object.keys(packArtPaths) as PackArtKey[];
